import { useEffect, useState, useCallback } from "react";
import api from "@/utils/axios-utils";
import { useAuthStore } from "@/store/useAuthStore";

export interface Trial {
  user_id: number;
  trial_start: string;
  trial_end: string;
  length_days: number;
  status: "active" | "expired" | "revoked";
  days_left: number;
  created_by: number;
  last_extended_at: string | null;
  extension_count: number;
  created_at: string;
  updated_at: string;
}

const POLL_MS = 60_000;

export function useMyTrial() {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const isDemo = useAuthStore((s) => s.user?.account_type === "demo");

  const [trial, setTrial] = useState<Trial | null | undefined>(() =>
    isDemo ? undefined : null,
  );

  const fetchTrial = useCallback(async () => {
    if (userId === null || !isDemo) {
      setTrial(null);
      return;
    }
    try {
      const { data } = await api.get<Trial | null>("/trials/me");
      setTrial(data ?? null);
    } catch {
      // Preserve last-known trial on transient failures so a network blip
      // doesn't unblur the page for an expired demo.
    }
  }, [userId, isDemo]);

  useEffect(() => {
    if (userId === null || !isDemo) {
      setTrial(null);
      return;
    }
    fetchTrial();
    const id = window.setInterval(() => {
      if (document.hidden) return;
      fetchTrial();
    }, POLL_MS);
    const onVisible = () => {
      if (!document.hidden) fetchTrial();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [userId, isDemo, fetchTrial]);

  return { trial, isDemo, refresh: fetchTrial };
}
